import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { api, API, formatApiError } from "../../lib/api";
import { Download, Upload, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";

const RESULT_FIELDS = [
  ["projects_created", "New projects"],
  ["projects_updated", "Updated projects"],
  ["stages_restored", "Stages restored"],
  ["documents_restored", "File records"],
  ["assignees_unmatched", "Assignees not found"],
];

export function PmBackupPanel({ onRestored }) {
  const [busy, setBusy] = useState(null);
  const [file, setFile] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const download = async () => {
    setBusy("download");
    try {
      const res = await fetch(`${API}/pm/backup/download`, { credentials: "include", headers: { "X-Requested-With": "XMLHttpRequest" } });
      if (!res.ok) throw new Error(`Download failed (HTTP ${res.status})`);
      const blob = await res.blob();
      const name = (res.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/)?.[1] || "flow-lifecycle-board.zip";
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: name });
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
      toast.success("Lifecycle Board backup downloaded");
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const reset = () => { setFile(null); setConfirm(false); if (inputRef.current) inputRef.current.value = ""; };

  const restore = async () => {
    setBusy("restore");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/pm/backup/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data); reset();
      toast.success(`Restored ${data.projects_created + data.projects_updated} projects`);
      onRestored?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Restore failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="pm-backup-panel">
      <div className="p-4 border border-[var(--line)] rounded-lg">
        <div className="font-semibold text-sm mb-1">Download board backup</div>
        <p className="text-xs muted mb-3">Every project with its stage, stage history and assignees, plus your custom stage names and order. Restore it on another server running Flow.</p>
        <button onClick={download} disabled={!!busy} className="btn-primary text-xs" data-testid="pm-backup-download-btn">
          {busy === "download" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Download .zip
        </button>
      </div>
      <div className="p-4 border border-[var(--line)] rounded-lg">
        <div className="font-semibold text-sm mb-1">Restore board backup</div>
        <p className="text-xs muted mb-3">Projects in the backup replace matching ones here; projects not in the backup are left untouched. Assignees are matched by email.</p>
        <input ref={inputRef} type="file" accept=".zip,application/zip" className="text-xs"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setConfirm(false); setResult(null); }}
          data-testid="pm-backup-restore-file" />
        {file && !confirm && (
          <button onClick={() => setConfirm(true)} className="btn-ghost text-xs mt-3" data-testid="pm-backup-restore-review">
            <AlertTriangle size={12} /> Review &amp; restore {file.name}
          </button>
        )}
        {confirm && (
          <div className="mt-3 p-3 rounded border border-[#B8721E] text-xs" data-testid="pm-backup-restore-confirm-panel">
            <p className="mb-3"><strong>Are you sure?</strong> Matching projects and the board's stage setup will be overwritten. Download a backup of this server first if you may need to undo.</p>
            <div className="flex gap-2">
              <button onClick={restore} disabled={!!busy} className="btn-primary text-xs" data-testid="pm-backup-restore-confirm">
                {busy === "restore" ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Restore now
              </button>
              <button onClick={reset} disabled={!!busy} className="btn-ghost text-xs" data-testid="pm-backup-restore-cancel">Cancel</button>
            </div>
          </div>
        )}
        {result && (
          <div className="mt-3 p-3 rounded border border-[#0F8B84]" data-testid="pm-backup-restore-result">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#0F8B84] mb-2"><CheckCircle2 size={14} /> Restore complete</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs">
              {RESULT_FIELDS.map(([k, label]) => (
                <div key={k}><div className="text-[10px] uppercase tracking-wider muted font-semibold">{label}</div>
                  <div className="font-mono text-sm" data-testid={`pm-backup-result-${k}`}>{result[k]}</div></div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
